// src/app/layout/UserArea.tsx
import { useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, LogOut, ShieldCheck, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { formatPersianNumber } from '../components/foundation';

/**
 * The "User Area" region called out in the layout spec. Reads the
 * signed-in user from useAuth() (authAdapter/localAuthProvider — no
 * @vanialabs/core-auth dependency) and owns the logout flow: clear
 * the session, then navigate to /login.
 *
 * Adds a lightweight account menu (پروفایل / امنیت / خروج). Profile
 * and Security are disabled placeholders — no destinations exist yet
 * — only logout is wired. No new UI/menu library: this is local
 * component state plus a blur-to-close div, matching the rest of the
 * app's "no new UI framework" direction.
 */
export function UserArea() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // TEMP DEBUG — remove once logout visibility is confirmed fixed.
  // Shows exactly what this component receives from useAuth() on every
  // render, so a missing/expired session is visible in devtools instead
  // of silently producing an empty header region.
  console.debug('[UserArea] session snapshot:', session);

  const user = session?.user;

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  // No usable user (e.g. a stale/corrupted persisted session that
  // hydrated without a `user`, or this region rendering in a state the
  // auth guard didn't anticipate). Previously this returned null and
  // silently removed the ONLY logout affordance in the header. Instead,
  // always leave a minimal way out: clear whatever session state exists
  // if there is one, otherwise send the person to /login.
  if (!user) {
    const hasStaleSession = session != null;
    return (
      <div className="shrink-0">
        <button
          type="button"
          onClick={hasStaleSession ? handleLogout : () => navigate('/login')}
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[var(--vania-text-secondary,#a6acb8)] transition-colors hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))] hover:text-[var(--vania-text-primary,#f5f6f8)]"
        >
          <LogOut size={14} strokeWidth={2.2} />
          {hasStaleSession ? 'خروج' : 'ورود'}
        </button>
      </div>
    );
  }

  const label = user.fullName ?? user.mobile;
  const userInitial = label.trim().charAt(0).toUpperCase();

  const closeMenuOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextTarget) && !menuRef.current?.contains(nextTarget)) {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (!isMenuOpen) {
      setMenuPosition(null);
      return;
    }

    const updatePosition = () => {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (!trigger) return;
      const gutter = 12;
      const width = Math.min(192, Math.max(1, window.innerWidth - gutter * 2));
      const right = Math.min(
        Math.max(gutter, window.innerWidth - width - gutter),
        Math.max(gutter, window.innerWidth - trigger.right),
      );
      setMenuPosition({ top: trigger.bottom + 8, right, width });
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    updatePosition();
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isMenuOpen]);

  return (
    <div ref={containerRef} className="relative min-w-0 shrink-0" onBlur={closeMenuOnBlur}>
      <button
        type="button"
        onClick={() => setIsMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        className="flex min-w-0 items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))]"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--vania-surface-2,rgba(255,255,255,0.08))] text-xs font-semibold text-[var(--vania-text-primary,#f5f6f8)]">
          {userInitial}
        </span>
        <span className="hidden min-w-0 flex-col items-start sm:flex">
          <span className="vania-user-area-label max-w-[9rem] truncate text-sm text-[var(--vania-text-primary,#f5f6f8)]">
            {label}
          </span>
          <span className="vania-user-area-role text-[10px] font-medium uppercase tracking-wide text-[var(--vania-accent,#5b8cff)]">
            Super Admin
          </span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2.2}
          className={`hidden shrink-0 text-[var(--vania-text-secondary,#a6acb8)] transition-transform sm:block ${
            isMenuOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isMenuOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-[var(--vania-z-dropdown,100)] max-h-[calc(100vh-24px)] overflow-y-auto rounded-xl border border-[var(--vania-border,rgba(255,255,255,0.1))] bg-[var(--vania-surface-1,rgba(20,22,30,0.95))] p-0 shadow-2xl backdrop-blur-xl"
          style={{ top: menuPosition.top, right: menuPosition.right, width: menuPosition.width }}
        >
          <div className="border-b border-[var(--vania-border,rgba(255,255,255,0.08))] px-3 py-2.5">
            <p className="truncate text-sm text-[var(--vania-text-primary,#f5f6f8)]">{label}</p>
            <p className="truncate text-xs text-[var(--vania-text-secondary,#a6acb8)]" dir="ltr">
              {formatPersianNumber(user.mobile)}
            </p>
          </div>

          <MenuItem icon={<User size={15} strokeWidth={2.2} />} label="پروفایل" disabled />
          <MenuItem icon={<ShieldCheck size={15} strokeWidth={2.2} />} label="امنیت" disabled />
          <MenuItem
            icon={<LogOut size={15} strokeWidth={2.2} />}
            label="خروج"
            onClick={handleLogout}
            tone="danger"
          />
        </div>,
        document.body,
      )}
    </div>
  );
}

interface MenuItemProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}

function MenuItem({ icon, label, onClick, disabled, tone = 'default' }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-right text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        tone === 'danger'
          ? 'text-[var(--vania-accent-rose,#ff6b81)] hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))]'
          : 'text-[var(--vania-text-primary,#f5f6f8)] hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))]'
      }`}
    >
      {icon}
      <span className="flex-1">{label}</span>
    </button>
  );
}
